/**
 * Google reCAPTCHA loader. No site key = tokens are empty and the API skips
 * verification in dev.
 *
 * The BrandonStrong keys are reCAPTCHA Enterprise score keys, which need the
 * `enterprise.js` script and the `grecaptcha.enterprise` namespace;
 * VITE_RECAPTCHA_ENTERPRISE=true selects that. A classic v3 key uses `api.js`.
 */
const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const ENTERPRISE = String(import.meta.env.VITE_RECAPTCHA_ENTERPRISE ?? '') === 'true';

interface GrecaptchaApi {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaApi & { enterprise?: GrecaptchaApi };
  }
}

let loading: Promise<void> | null = null;

export function isRecaptchaEnabled(): boolean {
  return Boolean(SITE_KEY);
}

export function recaptchaScriptUrl(siteKey: string, enterprise = ENTERPRISE): string {
  const file = enterprise ? 'enterprise.js' : 'api.js';
  return `https://www.google.com/recaptcha/${file}?render=${encodeURIComponent(siteKey)}`;
}

function api(): GrecaptchaApi | undefined {
  const g = window.grecaptcha;
  if (!g) return undefined;
  return ENTERPRISE ? g.enterprise : g;
}

function load(): Promise<void> {
  if (!SITE_KEY) return Promise.resolve();
  if (api()) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = recaptchaScriptUrl(SITE_KEY);
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
    document.head.appendChild(script);
  });
  return loading;
}

/** Warm the script early (call on checkout mount). */
export function preloadRecaptcha(): void {
  void load().catch(() => undefined);
}

export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  if (!SITE_KEY) return undefined;
  await load();
  const grecaptcha = api();
  if (!grecaptcha) throw new Error('reCAPTCHA unavailable');
  await new Promise<void>((resolve) => grecaptcha.ready(resolve));
  return grecaptcha.execute(SITE_KEY, { action });
}
